import { ScatterPlot as NivoScatterPlot, ScatterPlotDatum, ScatterPlotNode, ScatterPlotNodeProps } from '@nivo/scatterplot';
import { useContext, useLayoutEffect, useRef } from 'react';

import { Text, type DateRangeType } from '@medly-components/core';
import { useTheme } from 'styled-components';
import { ReactComponent as DownArrow } from './Down.svg';
import { EmptyScatterPlot } from './EmptyScatterPlot';
import { NodeType, useScatterPlotGraph } from './hooks/useScatterPlotGraphHook';
import { RangeDiv, ScatterPlotWrapper } from './ScatterPlot.styled';
import { getScatterPlotCommonProp } from './scatterPlotCommonProps';
import { ScatterPlotContext, ScatterPlotContextProvider } from './ScatterPlotContext';
import { Tooltip } from './Tooltip/Tooltip';
import { ScatterPlotProps } from './types';
import { ReactComponent as UpArrow } from './Up.svg';

const MIN_HEIGHT = 300;
const MIN_WIDTH = 800;

export const ScatterPlot = <RawDatum extends ScatterPlotDatum>(
    props: ScatterPlotProps<RawDatum> & {
        tooltipContent?: (content: RawDatum) => void;
        showRange?: boolean;
        emptyText?: string;
        graphDates?: DateRangeType;
    }
) => {
    return (
        <ScatterPlotContextProvider>
            <ScatterPlotGraph {...props} />
        </ScatterPlotContextProvider>
    );
};

const ScatterPlotGraph = <RawDatum extends ScatterPlotDatum>({
    hideFirstRowAndColumnLabel,
    height = MIN_HEIGHT,
    nodeComponent,
    tooltipContent,
    showRange,
    ...props
}: ScatterPlotProps<RawDatum> & {
    tooltipContent?: (content: RawDatum) => void;
    showRange?: boolean;
    emptyText?: string;
    graphDates?: DateRangeType;
}) => {
    const { dimension, setDimension } = useContext(ScatterPlotContext);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const defaultProps = {
        ...dimension,
        ...getScatterPlotCommonProp<RawDatum>()
    };
    const { data, total, moveUp, moveDown, range } = useScatterPlotGraph({ plotData: props.data });
    const theme = useTheme();
    useLayoutEffect(() => {
        const handleDimensionChange = () =>
            setDimension({
                width: Math.max(wrapperRef.current?.clientWidth || 0, MIN_WIDTH),
                height: height || MIN_HEIGHT
            });
        window.addEventListener('resize', handleDimensionChange);
        handleDimensionChange();
        return () => window.removeEventListener('resize', handleDimensionChange);
    }, []);

    const getNodeComponent: ScatterPlotNode<RawDatum> = (props: ScatterPlotNodeProps<RawDatum>) => {
        const nodeData: any = props.node.data;
        const node = props.node;

        if (nodeData.nodeType === NodeType.EMPTY_NODE) {
            return <g transform={`translate(${node.x - 32},${node.y - 32})`}></g>;
        }
        if (nodeData?.nodeType === NodeType.UP_ARROW) {
            return (
                <g transform={`translate(${node.x - 32},${node.y - 32})`}>
                    <UpArrow style={{ cursor: 'pointer' }} onClick={moveUp} />
                </g>
            );
        }
        if (nodeData?.nodeType === NodeType.DOWN_ARROW) {
            return (
                <g transform={`translate(${node.x - 32},${node.y - 32})`}>
                    <DownArrow style={{ cursor: 'pointer' }} onClick={moveDown} />
                </g>
            );
        }
        return nodeComponent ? nodeComponent(props) : null;
    };

    return (
        <ScatterPlotWrapper hideFirstRowAndColumnLabel={hideFirstRowAndColumnLabel} ref={wrapperRef}>
            {!data.length ? (
                <EmptyScatterPlot {...{ ...defaultProps, ...props }} />
            ) : (
                <NivoScatterPlot
                    {...{ ...defaultProps, ...props, data }}
                    nodeComponent={getNodeComponent}
                    tooltip={
                        tooltipContent
                            ? props => {
                                  return <Tooltip content={tooltipContent} {...props} />;
                              }
                            : undefined
                    }
                />
            )}
            {showRange && total ? (
                <RangeDiv>
                    <Text textVariant="body3" style={{ color: theme.colors.grey['700'] }}>
                        {range.min + 1} - {Math.min(range.max, total)} / {total}
                    </Text>
                </RangeDiv>
            ) : null}
        </ScatterPlotWrapper>
    );
};

ScatterPlot.displayName = 'ScatterPlot';
